import { coalesceGet, invalidateAroundWrite, invalidateCoalesced } from "./coalesceGet";
import { invalidatePathProbe } from "./pathProbeCache";
import { getHeadersApplication } from "./middleware";
import { handleServiceError } from "./errorHandler";
import { CSSProperties } from "react";
import { REACT_APP_BACKEND_API } from "../config";
import { captureApiError, measureApiCall, trackApplicationState } from "../utils/sentry";
import { trackApplicationStep } from "../utils/amplitude";

function applicationReadKeys(quoteId: string): string[] {
  return [`application:${quoteId}`, `form-summary:${quoteId}`, `qfs-summary:${quoteId}`];
}

function dropApplicationProbe(quoteId: string): void {
  invalidatePathProbe(quoteId);
}

const BASE_URL = REACT_APP_BACKEND_API;

export enum ApplicationStatus {
  IN_PROGRESS = 'in progress',
  //COMPLETE = 'complete',
  IN_SUMMARY = 'in summary',
  UPLOAD_DOCUMENTS = 'upload documents',
  IN_SIGN = 'in sign',
  SIGNED = 'signed'
}

export const isValidApplicationStatus = (status: string): status is ApplicationStatus => {
  return Object.values(ApplicationStatus).includes(status as ApplicationStatus);
};

export const parseApplicationStatus = (status: string): ApplicationStatus => {
  if (isValidApplicationStatus(status)) {
    return status;
  }
  throw new Error(`Invalid status: ${status}. Valid statuses are: ${Object.values(ApplicationStatus).join(', ')}`);
};

const getForm = (product: string, region?: string): string => {
  if (region === 'br') {
    return '4b3c8d9f-6724-43f3-a04d-0dc6fb0ed5a1';
  }
  if (region === 'mx') {
    return '9a9b9085-f72d-48c9-b7c6-0935ac9128f7';
  }
  switch (product) {
    case 'easy_term':
      return 'ef729bfe-8276-42ab-8ce1-bc7e869fe5d5';
    case 'master_term':
      return 'ef729bfe-8276-42ab-8ce1-bc7e869fe5d5';
    default:
      throw new Error('Product not found');
  }
}

interface ApplicationRequest {
  quote_id: string;
  form_id: string;
  language: string;
  json?: any;
}

interface PageInfo {
  id: number;
  template_page_id: string;
}

type ApplicationResponse = {
  id: number;
  quote_id: string;
  status: string;
  last_page_template: string;
  last_page_id: number;
  language: string;
  pages: PageInfo[];
  is_test?: boolean;
}

export type Traductor = {
  en: string;
  es: string;
  pt?: string;
}

export interface TemplateForm {
  id: string;
  form_id: string;
  description: string;
  order: number;
  json: any;
}

export interface TemplateField {
  id: number;
  question_id: string;
  title: Traductor;
  options: Option[];
  placeholder: Traductor;
  length?: number;
  value_default: string;
  information: Traductor;
  classname: string;
  classname_phone?: string;
  classname_tablet?: string;
  ui: string;
  order: number;
  hidden: boolean;
  required: boolean;
  disabled?: boolean;
  format?: string;
  value?: string;
  option_clear?: string;
  error?: boolean;
  minDate?: string;
  maxDate?: string;
  multipleChoiceColumns?: 1 | 2;
  maxFiles?: number;
  //currency example: MXN, USD $
  currency?: string;
  //minCurrency example: 1000
  minCurrency?: number;
  //maxCurrency example: 1000000
  maxCurrency?: number;
  minUnitValue?: number;
  maxUnitValue?: number;
  minNumber?: number;
  maxNumber?: number;
  minLength?: number;
  maxLength?: number;
  insuredCpf?: string;
}

export interface TemplateGroup {
  id: number;
  group_id: string;
  title: Traductor;
  with_fieldset: boolean;
  color: string;
  order: number;
  fields: TemplateField[];
  hidden: boolean;
  trash: boolean;
  required?: boolean;
  correlative?: boolean;
}

export interface TemplateSection {
  id: number;
  section_id: string;
  title: Traductor;
  color: string;
  text_align: string;
  order: number;
  required: boolean;
  groups: TemplateGroup[];
  hidden: boolean;
  style: CSSProperties;
}

export interface TemplatePage {
  id?: number;
  page_id?: string;
  form_id?: string;
  description?: string;
  sections?: TemplateSection[];
  json?: any;
  status?: string;
  mark?: string;
  quote_id?: string;
}

export interface UpdateStatusRequest {
  status: ApplicationStatus;
}

export interface UpdateStatusResponse {
  message: string;
}

export const startedRequest = async (product: string, quoteID: string, language: string, region?: string): Promise<ApplicationResponse> => {
  const request: ApplicationRequest = {
    quote_id: quoteID,
    form_id: getForm(product, region),
    language: language
  };
  dropApplicationProbe(quoteID);
  try {
    return await invalidateAroundWrite(applicationReadKeys(quoteID), async () => {
    const response = await fetch(`${BASE_URL}application`, {
      method: 'POST',
      headers: getHeadersApplication(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json() as ApplicationResponse;
    });
  } catch (error) {
    handleServiceError(error);
    throw error;
  } finally {
    dropApplicationProbe(quoteID);
  }
};

export const findTemplatePage = async (pageID: string): Promise<TemplateForm> => {
  try {
    const response = await fetch(`${BASE_URL}pages/${pageID}`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json() as TemplateForm;
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const createPage = async (appID: number, body: any): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}application/${appID}/page`, {
      method: 'POST',
      headers: getHeadersApplication(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const recoveryPage = async (pageID: number): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}application/page/${pageID}`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const saveAnswersOfPage = async (appID: number, body: any): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}application/page/${appID}`, {
      method: 'PUT',
      headers: getHeadersApplication(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const removePage = async (pageID: number): Promise<any> => {
  try {
    const response = await fetch(`${BASE_URL}application/page/${pageID}`, {
      method: 'DELETE',
      headers: getHeadersApplication(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const getApplicationSummary = async (quoteID: string): Promise<any> => {
  // In-flight only. The confirmation screen retries this GET while it waits for
  // page blobs; a stored response would freeze that loop on the first empty body.
  return coalesceGet(
    `form-summary:${quoteID}`,
    async () => {
      try {
        const response = await fetch(`${BASE_URL}form-applications/summary/${quoteID}`, {
          method: 'GET',
          headers: getHeadersApplication(),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Error: ${response.status} - ${errorData.message}`);
        }
        return await response.json();
      } catch (error) {
        handleServiceError(error);
        throw error;
      }
    },
    { ttlMs: 0 },
  );
};

// Tipo para la respuesta del endpoint GET /application?quoteId=
export interface GetApplicationResponse {
  data: {
    id: number;
    quote_id: string;
    status: string;
    app_number?: string;
    last_page_template?: string;
    last_page_id?: number;
    language?: string;
    is_test?: boolean;
    pages?: PageInfo[];
  } | null;
  found: boolean;
}

/**
 * Obtiene la información de una aplicación por quoteId (solo lectura, no crea)
 * @param quoteID ID de la cotización
 * @returns Información de la aplicación con flag 'found'
 */
export const getApplicationByQuoteId = async (
  quoteID: string,
  options?: { fresh?: boolean },
): Promise<GetApplicationResponse> => {
  // QuoteContext, the resume check and the intake gate each GET this on boot.
  // `fresh` drops that boot cache: a deferred /quote write must see a status
  // that changed while the client was on /confirmation, not the 4s snapshot
  // from opening /application.
  if (options?.fresh) invalidateCoalesced(`application:${quoteID}`);
  return coalesceGet(`application:${quoteID}`, async () => {
    try {
      const response = await fetch(`${BASE_URL}application?quoteId=${quoteID}`, {
        method: 'GET',
        headers: getHeadersApplication(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error: ${response.status} - ${errorData.message}`);
      }
      return await response.json() as GetApplicationResponse;
    } catch (error) {
      handleServiceError(error);
      throw error;
    }
  });
};

export const updateApplicationStatus = async (
  quoteId: string,
  status: ApplicationStatus
): Promise<UpdateStatusResponse> => {
  const endpoint = `application/${quoteId}/status`;

  dropApplicationProbe(quoteId);
  try {
    return await invalidateAroundWrite(applicationReadKeys(quoteId), async () => {
    // Monitoreo de rendimiento y captura de errores integrada
    return await measureApiCall(
      endpoint,
      'PUT',
      async () => {
        const request: UpdateStatusRequest = {
          status: status
        };

        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        headers.append('x-user', 'SYSTEM');

        const response = await fetch(`${BASE_URL}${endpoint}`, {
          method: 'PUT',
          headers: headers,
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Error: ${response.status} - ${errorData.message}`);
        }

        trackApplicationState(quoteId, status, undefined, {
          operation: 'updateApplicationStatus',
          status: status,
          timestamp: new Date().toISOString()
        });
        trackApplicationStep(status, quoteId);
        return await response.json() as UpdateStatusResponse;
      }
    );
    });
  } catch (error) {
    // Captura de error específica para esta API
    captureApiError(error, endpoint, 'PUT', {
      quoteId,
      newStatus: status
    });

    handleServiceError(error);
    throw error;
  } finally {
    dropApplicationProbe(quoteId);
  }
};

//OLD

export type Traduction = {
  [key: string]: string;
}

export type Option = {
  key: string;
  value: string | Traduction;
}

export type Question = {
  id: string;
  title: Traduction;
  placeholder: Traduction;
  tag: string;
  classname: string;
  ui: string;
  order: number;
  visible: boolean;
  required: boolean;
  options: Option[];
  format: string;
}

export interface Form {
  form_id: string;
  title: string;
  is_active: boolean;
  questions: Question[];
}

export interface FormApplication {
  id: number;
  form_id: string;
  application_id: string;
  status: string;
  last_page: number;
}

export interface Answer {
  form_id: string;
  group_id: string;
  question_id: string;
  page: number;
  order: number;
  answer: string;
  extra_id: string;
}

export interface Answers {
  page: number;
  groups_with_extra_id: string[];
  answers: Answer[];
}

export interface SectionAnswer {
  form_id: string;
  question_id: string;
  group_id: string;
  page: number;
  order: number;
  answer: string;
}

export const fetchApplication = async (formID: string): Promise<Form> => {
  try {
    const response = await fetch(`${BASE_URL}forms/${formID}`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const startedApplication = async (formID: string, applicationID: string): Promise<FormApplication> => {
  try {
    const response = await fetch(`${BASE_URL}form-applications`, {
      method: 'POST',
      headers: getHeadersApplication(),
      body: JSON.stringify({ form_id: formID, application_id: applicationID }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Error: ${response.status} - ${errorData.message}`);
    }

    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
};

export const saveAnswers = async (id: number, page: number, groupsWithExtraID: string[], answers: Answer[]) => {
  const objAnswers: Answers = {
    page: page,
    groups_with_extra_id: groupsWithExtraID,
    answers
  };
  const response = await fetch(`${BASE_URL}form-applications/${id}/answers`, {
    method: 'POST',
    headers: getHeadersApplication(),
    body: JSON.stringify(objAnswers),
  });

  if (!response.ok) {
    throw new Error('Error al guardar las respuestas');
  }
  return;
}

export const recoveryAnswers = async (id: number, page: number): Promise<Answer[]> => {
  try {
    const response = await fetch(`${BASE_URL}form-applications/${id}/answers/${page}`, {
      method: 'GET',
      headers: getHeadersApplication(),
    });

    if (!response.ok) {
      throw new Error('Error al guardar las respuestas');
    }
    return await response.json();
  } catch (error) {
    handleServiceError(error);
    throw error;
  }
}

export const orderedOptions = (
  question: TemplateField,
  language?: 'pt' | 'en' | 'es'
) => {
  const lang = language?.toLowerCase() as 'pt' | 'en' | 'es' | undefined;

  const transform = question?.options?.map((a: any) => {
    // Si se proporciona language y existe en el objeto, se prioriza,
    // de lo contrario se mantiene el comportamiento actual (pt > en > es)
    const preferredLabel = lang && a?.value?.[lang] ? a?.value?.[lang] : undefined;

    return {
      value: a?.key,
      label:
        preferredLabel ||
        a?.value?.pt ||
        a?.value?.en ||
        a?.value?.es,
    };
  });

  if (question?.ui === 'dropdown') {
    return transform?.sort((a, b) => a.label.localeCompare(b.label));
  }

  return transform;
}

export const unorderedOptions = (
  question: TemplateField,
  language?: 'pt' | 'en' | 'es'
) => {
  const lang = language?.toLowerCase() as 'pt' | 'en' | 'es' | undefined;

  const transform = question?.options?.map((a: any) => {
    // Si se proporciona language y existe en el objeto, se prioriza,
    // de lo contrario se mantiene el comportamiento actual (pt > en > es)
    const preferredLabel = lang && a?.value?.[lang] ? a?.value?.[lang] : undefined;

    return {
      value: a?.key,
      label:
        preferredLabel ||
        a?.value?.pt ||
        a?.value?.en ||
        a?.value?.es,
    };
  });

  // Devuelve las opciones sin ordenar, manteniendo el orden original
  return transform;
}